import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Palmtree, Star, MapPin, Clock, Check, X, Plane, Hotel, Car, Utensils,
  Camera, FileText, Loader2, Sparkles, Calendar, Users, ArrowRight,
} from "lucide-react";
import Header, { TopBar } from "@/components/Header";
import Footer from "@/components/Footer";
import { formatINR } from "@/mock";
import { apiListHolidays, apiGetHoliday, apiHolidayEnquiry } from "@/api";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";

const INC_ICON = {
  "Return Flights": Plane, "Hotel Stay": Hotel, "Airport Transfers": Car,
  "Daily Breakfast": Utensils, Sightseeing: Camera, "Visa Assistance": FileText,
};

const HolidayCard = ({ p, onView, onAddCart }) => {
  const off = p.oldPrice > p.price ? Math.round(((p.oldPrice - p.price) / p.oldPrice) * 100) : 0;
  return (
    <div data-testid={`holiday-card-${p.package_id}`}
      className="group overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
      <div className="relative h-52 overflow-hidden">
        <img src={p.image} alt={p.destination} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
        <span className="absolute left-3 top-3 rounded-full bg-[#1B1F5B] px-3 py-1 text-[11px] font-bold text-white">{p.nights}N / {p.days}D</span>
        {off > 0 && <span className="absolute right-3 top-3 rounded-full bg-[#F5B32B] px-2.5 py-1 text-[11px] font-extrabold text-[#0A0F2E]">{off}% OFF</span>}
        {p.featured && <span className="absolute bottom-3 left-3 flex items-center gap-1 rounded-full bg-white/90 px-2.5 py-1 text-[10px] font-extrabold text-[#0E7C6B]"><Sparkles className="h-3 w-3" /> Bestseller</span>}
        <div className="absolute bottom-3 right-3 flex items-center gap-1 rounded-full bg-white/90 px-2 py-0.5 text-xs font-bold text-emerald-600"><Star className="h-3.5 w-3.5 fill-emerald-500 text-emerald-500" /> {p.rating}</div>
      </div>
      <div className="p-4">
        <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-[#0E7C6B]"><MapPin className="h-3 w-3" /> {p.destination}, {p.country}</div>
        <h3 className="mt-1 text-base font-extrabold text-slate-800">{p.title}</h3>
        <p className="mt-1 line-clamp-2 text-xs text-slate-400">{p.summary}</p>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {(p.inclusions || []).slice(0, 3).map((i) => (
            <span key={i} className="rounded-md bg-slate-50 px-2 py-0.5 text-[10px] font-semibold text-slate-500">{i}</span>
          ))}
        </div>
        <div className="mt-3 flex items-end justify-between border-t border-dashed pt-3">
          <div>
            {off > 0 && <p className="text-xs font-semibold text-slate-400 line-through">{formatINR(p.oldPrice)}</p>}
            <p className="text-lg font-extrabold text-[#1B1F5B]">{formatINR(p.price)}<span className="block text-[10px] font-medium text-slate-400">per person</span></p>
          </div>
          <button data-testid={`holiday-view-${p.package_id}`} onClick={() => onView(p)}
            className="flex items-center gap-1 rounded-lg bg-[#F5B32B] px-4 py-2 text-xs font-extrabold text-[#0A0F2E] transition-colors hover:bg-[#E89A10]">
            View Details <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
        <button data-testid={`holiday-add-cart-${p.package_id}`} onClick={() => onAddCart(p)}
          className="mt-2 w-full rounded-lg border border-[#1B1F5B]/20 py-1.5 text-xs font-bold text-[#1B1F5B] transition-colors hover:bg-[#1B1F5B]/5">
          + Add to Trip
        </button>
      </div>
    </div>
  );
};

const HolidayDetail = ({ id, onClose }) => {
  const [p, setP] = useState(null);
  const [enq, setEnq] = useState({ name: "", email: "", phone: "", travelDate: "", pax: 2, message: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { apiGetHoliday(id).then(setP).catch(() => toast.error("Could not load package")); }, [id]);

  const submit = async () => {
    if (!enq.name || !enq.email || !enq.phone) return toast.error("Please fill name, email & phone");
    setBusy(true);
    try {
      await apiHolidayEnquiry({ packageId: id, ...enq, pax: Number(enq.pax) || 2 });
      toast.success("Enquiry submitted! Our travel expert will call you shortly.");
      onClose();
    } catch { toast.error("Could not submit enquiry"); }
    finally { setBusy(false); }
  };

  if (!p) return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <Loader2 className="h-8 w-8 animate-spin text-white" />
    </div>
  );
  const off = p.oldPrice > p.price ? Math.round(((p.oldPrice - p.price) / p.oldPrice) * 100) : 0;
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-3 md:p-6" data-testid="holiday-detail-modal">
      <div className="my-4 w-full max-w-4xl overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="relative h-56 md:h-72">
          <img src={p.image} alt={p.destination} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
          <button data-testid="holiday-detail-close" onClick={onClose} className="absolute right-3 top-3 rounded-full bg-white/90 p-2 text-slate-700 hover:bg-white"><X className="h-5 w-5" /></button>
          <div className="absolute bottom-4 left-5 text-white">
            <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide"><MapPin className="h-3.5 w-3.5" /> {p.destination}, {p.country}</div>
            <h2 className="mt-1 font-[Poppins] text-2xl font-extrabold md:text-3xl">{p.title}</h2>
            <div className="mt-2 flex items-center gap-3 text-xs font-semibold">
              <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {p.nights}N / {p.days}D</span>
              <span className="flex items-center gap-1"><Star className="h-3.5 w-3.5 fill-[#F5B32B] text-[#F5B32B]" /> {p.rating}</span>
              <span className="rounded-full bg-white/20 px-2 py-0.5">{p.category}</span>
            </div>
          </div>
        </div>

        <div className="grid gap-6 p-5 md:grid-cols-[1fr_320px] md:p-7">
          <div>
            <p className="text-sm text-slate-600">{p.summary}</p>

            {p.highlights?.length > 0 && (
              <div className="mt-5">
                <h3 className="text-sm font-extrabold text-slate-800">Highlights</h3>
                <div className="mt-2 grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                  {p.highlights.map((h) => (
                    <div key={h} className="flex items-center gap-2 text-xs text-slate-600"><Check className="h-3.5 w-3.5 shrink-0 text-[#0E7C6B]" /> {h}</div>
                  ))}
                </div>
              </div>
            )}

            {p.inclusions?.length > 0 && (
              <div className="mt-5">
                <h3 className="text-sm font-extrabold text-slate-800">What's Included</h3>
                <div className="mt-2 flex flex-wrap gap-2">
                  {p.inclusions.map((i) => {
                    const Icon = INC_ICON[i] || Check;
                    return <span key={i} className="flex items-center gap-1.5 rounded-lg bg-[#0E7C6B]/5 px-3 py-1.5 text-xs font-semibold text-[#0A5348]"><Icon className="h-3.5 w-3.5" /> {i}</span>;
                  })}
                </div>
              </div>
            )}

            {p.itinerary?.length > 0 && (
              <div className="mt-5">
                <h3 className="text-sm font-extrabold text-slate-800">Day-wise Itinerary</h3>
                <div className="mt-3 space-y-3">
                  {p.itinerary.map((d) => (
                    <div key={d.day} className="flex gap-3">
                      <div className="flex flex-col items-center">
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#1B1F5B] text-xs font-extrabold text-white">{d.day}</span>
                        {d.day < p.itinerary.length && <span className="my-1 w-px flex-1 bg-slate-200" />}
                      </div>
                      <div className="pb-2">
                        <p className="text-sm font-bold text-slate-700">Day {d.day}: {d.title}</p>
                        <p className="text-xs text-slate-400">{d.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Enquiry card */}
          <div className="h-fit rounded-2xl border border-slate-100 bg-slate-50/60 p-5 md:sticky md:top-4">
            <div className="flex items-end gap-2">
              {off > 0 && <p className="text-sm font-semibold text-slate-400 line-through">{formatINR(p.oldPrice)}</p>}
              {off > 0 && <span className="rounded bg-[#F5B32B]/20 px-1.5 text-[11px] font-extrabold text-[#B57A00]">{off}% OFF</span>}
            </div>
            <p className="text-2xl font-extrabold text-[#1B1F5B]">{formatINR(p.price)}<span className="text-xs font-medium text-slate-400"> /person</span></p>
            <div className="mt-4 space-y-2.5">
              <input data-testid="enq-name" value={enq.name} onChange={(e) => setEnq({ ...enq, name: e.target.value })} placeholder="Your name" className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-[#0E7C6B]" />
              <input data-testid="enq-email" type="email" value={enq.email} onChange={(e) => setEnq({ ...enq, email: e.target.value })} placeholder="Email" className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-[#0E7C6B]" />
              <input data-testid="enq-phone" value={enq.phone} onChange={(e) => setEnq({ ...enq, phone: e.target.value })} placeholder="Mobile number" className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-[#0E7C6B]" />
              <div className="grid grid-cols-2 gap-2">
                <div className="relative"><Calendar className="pointer-events-none absolute left-2.5 top-3 h-4 w-4 text-slate-400" /><input data-testid="enq-date" type="date" value={enq.travelDate} onChange={(e) => setEnq({ ...enq, travelDate: e.target.value })} className="w-full rounded-xl border border-slate-200 py-2.5 pl-8 pr-2 text-xs outline-none focus:border-[#0E7C6B]" /></div>
                <div className="relative"><Users className="pointer-events-none absolute left-2.5 top-3 h-4 w-4 text-slate-400" /><input data-testid="enq-pax" type="number" min={1} value={enq.pax} onChange={(e) => setEnq({ ...enq, pax: e.target.value })} placeholder="Pax" className="w-full rounded-xl border border-slate-200 py-2.5 pl-8 pr-2 text-sm outline-none focus:border-[#0E7C6B]" /></div>
              </div>
              <textarea data-testid="enq-message" value={enq.message} onChange={(e) => setEnq({ ...enq, message: e.target.value })} rows={2} placeholder="Any preferences? (optional)" className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-[#0E7C6B]" />
              <button data-testid="enq-submit" onClick={submit} disabled={busy} className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#F5B32B] py-3 text-sm font-extrabold text-[#0A0F2E] hover:bg-[#E89A10] disabled:opacity-60">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send Enquiry"}
              </button>
              <p className="text-center text-[10px] text-slate-400">Free consultation · No booking charges</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const HolidaysPage = () => {
  const navigate = useNavigate();
  const { add: addToCart } = useCart();
  const [holidays, setHolidays] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState("all");
  const [q, setQ] = useState("");
  const [budget, setBudget] = useState(0);
  const [detailId, setDetailId] = useState(null);

  useEffect(() => {
    setLoading(true);
    apiListHolidays().then((d) => { setHolidays(d.holidays || []); setCats(d.categories || []); })
      .catch(() => {}).finally(() => setLoading(false));
  }, []);

  const shown = holidays
    .filter((p) => cat === "all" || p.category === cat)
    .filter((p) => !q || `${p.title} ${p.destination} ${p.country}`.toLowerCase().includes(q.toLowerCase()))
    .filter((p) => !budget || p.price <= budget);

  return (
    <div className="min-h-screen bg-[#F5F7FB]">
      <TopBar />
      <Header />
      {/* Hero */}
      <div className="relative overflow-hidden bg-gradient-to-r from-[#1B1F5B] via-[#252A75] to-[#0E7C6B] py-12 text-white">
        <div className="mx-auto max-w-7xl px-4">
          <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-[#F5B32B]"><Palmtree className="h-4 w-4" /> Holiday Packages</div>
          <h1 className="mt-2 font-[Poppins] text-3xl font-extrabold md:text-4xl">Handcrafted Holidays, Unforgettable Memories</h1>
          <p className="mt-1 text-sm text-white/70">Flights + Hotels + Transfers + Sightseeing — all in one price</p>
          <div className="mt-5 flex max-w-xl overflow-hidden rounded-xl bg-white shadow-lg">
            <input data-testid="holiday-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search destination — Goa, Dubai, Bali…" className="flex-1 px-4 py-3 text-sm text-slate-700 outline-none" />
            <span className="flex items-center px-4 text-slate-400"><MapPin className="h-4 w-4" /></span>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-8">
        {/* Filters */}
        <div className="mb-6 flex flex-wrap items-center gap-2" data-testid="holiday-filters">
          <button onClick={() => setCat("all")} className={`rounded-full border px-4 py-1.5 text-xs font-bold transition-colors ${cat === "all" ? "border-[#1B1F5B] bg-[#1B1F5B] text-white" : "border-slate-200 bg-white text-slate-500 hover:border-[#1B1F5B]/40"}`}>All</button>
          {cats.map((c) => (
            <button key={c} data-testid={`holiday-cat-${c}`} onClick={() => setCat(c)} className={`rounded-full border px-4 py-1.5 text-xs font-bold transition-colors ${cat === c ? "border-[#1B1F5B] bg-[#1B1F5B] text-white" : "border-slate-200 bg-white text-slate-500 hover:border-[#1B1F5B]/40"}`}>{c}</button>
          ))}
          <span className="mx-1 h-4 w-px bg-slate-200" />
          <select data-testid="holiday-budget" value={budget} onChange={(e) => setBudget(Number(e.target.value))} className="rounded-full border border-slate-200 bg-white px-4 py-1.5 text-xs font-bold text-slate-500 outline-none">
            <option value={0}>Any Budget</option>
            <option value={20000}>Under ₹20k</option>
            <option value={40000}>Under ₹40k</option>
            <option value={60000}>Under ₹60k</option>
            <option value={100000}>Under ₹1L</option>
          </select>
          <span className="ml-auto text-xs font-semibold text-slate-400">{shown.length} packages</span>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#0E7C6B]" /></div>
        ) : shown.length === 0 ? (
          <p className="py-20 text-center text-sm text-slate-400" data-testid="holiday-empty">No packages match your search. Try a different destination or budget.</p>
        ) : (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {shown.map((p) => <HolidayCard key={p.package_id} p={p} onView={(x) => navigate(`/package-details/${x.slug || x.package_id}`)} onAddCart={(x) => addToCart({ kind: "holiday", title: x.title, route: `${x.destination}, ${x.country}`, date: "", price: x.price, pax: 2, meta: { packageId: x.package_id } })} />)}
          </div>
        )}
      </div>

      {detailId && <HolidayDetail id={detailId} onClose={() => setDetailId(null)} />}
      <Footer />
    </div>
  );
};

export default HolidaysPage;
